/**
 * CSV-Bausteine für die Artikel-Zuordnung (Export → in Excel ausfüllen → Import).
 *
 * Reine Funktionen ohne DOM und ohne Supabase, damit sich das Format-Verhalten direkt testen
 * lässt. Die Karte in den Einstellungen (`views/tabs/article-assign-io.ts`) nutzt sie.
 *
 * Format: Semikolon-getrennt, UTF-8 mit BOM, CRLF. Das ist, was deutsches Excel unter „CSV"
 * schreibt und ohne Import-Assistent wieder korrekt öffnet.
 *
 * Zahlenfalle: Ohne Gegenmaßnahme macht Excel aus der EAN 4006381333931 die Anzeige
 * 4,00638E+12 und speichert sie so auch zurück — die Nummer wäre zerstört. Deshalb werden
 * SKU, EAN und Vario-Artikelnummer als `="4006381333931"` geschrieben; Excel behandelt das
 * als Text. Der Parser streift die Hülle wieder ab, akzeptiert aber auch Dateien ohne sie
 * (etwa aus LibreOffice oder von Hand gepflegt).
 */

/** Spaltenköpfe der Export-Datei, in genau dieser Reihenfolge. */
export const ASSIGN_CSV_HEADERS = [
  'SKU',
  'Vario-SKU',
  'EAN',
  'Titel',
  'Storefront',
  'Vario-Artikelnummer',
] as const;

/** Zusätzliche Spalte der Fehler-Datei, die nach einem Lauf heruntergeladen werden kann. */
export const ASSIGN_CSV_ERROR_HEADER = 'Fehler';

export interface AssignCsvRow {
  sku: string;
  varioSkuCurrent: string;
  ean: string;
  title: string;
  storefront: string;
  varioNumber: string;
}

export interface ParsedAssignRow {
  /** 0-basierte Position in der Liste, die an die RPC geht — deren `rows[].i` zeigt hierauf. */
  index: number;
  /** Zeilennummer in der Datei inklusive Kopfzeile, damit der Kunde die Stelle wiederfindet. */
  line: number;
  sku: string;
  ean: string;
  storefront: string;
  varioNumber: string;
}

export type ParseResult =
  | { ok: true; rows: ParsedAssignRow[] }
  | { ok: false; error: string };

// ---------------------------------------------------------------------------
// Erzeugen
// ---------------------------------------------------------------------------

/** Maskiert einen Wert für die CSV-Zelle: Quotes verdoppeln, bei Sonderzeichen einpacken. */
function cell(value: string): string {
  const v = value ?? '';
  if (v === '') return '';
  if (/[";\r\n]/.test(v)) return `"${v.replaceAll('"', '""')}"`;
  return v;
}

/**
 * Zelle für Werte, die Excel sonst in eine Zahl umdeutet (EAN, SKU, Artikelnummer).
 * `="…"` ist die Schreibweise, die Excel als Textformel liest und unverändert stehen lässt.
 */
function textCell(value: string): string {
  const v = value ?? '';
  if (v === '') return '';
  return `"=""${v.replaceAll('"', '""')}"""`;
}

/** Baut die Export-Datei. Rückgabe inklusive BOM und CRLF, direkt als Blob-Inhalt verwendbar. */
export function buildAssignCsv(rows: ReadonlyArray<AssignCsvRow>): string {
  const lines = [ASSIGN_CSV_HEADERS.join(';')];
  for (const r of rows) {
    lines.push(
      [
        textCell(r.sku),
        textCell(r.varioSkuCurrent),
        textCell(r.ean),
        cell(r.title),
        cell(r.storefront),
        textCell(r.varioNumber),
      ].join(';'),
    );
  }
  return `﻿${lines.join('\r\n')}\r\n`;
}

/** Wie `buildAssignCsv`, zusätzlich mit einer Fehlerspalte je Zeile. */
export function buildAssignErrorCsv(
  rows: ReadonlyArray<AssignCsvRow & { error: string }>,
): string {
  const lines = [[...ASSIGN_CSV_HEADERS, ASSIGN_CSV_ERROR_HEADER].join(';')];
  for (const r of rows) {
    lines.push(
      [
        textCell(r.sku),
        textCell(r.varioSkuCurrent),
        textCell(r.ean),
        cell(r.title),
        cell(r.storefront),
        textCell(r.varioNumber),
        cell(r.error),
      ].join(';'),
    );
  }
  return `﻿${lines.join('\r\n')}\r\n`;
}

// ---------------------------------------------------------------------------
// Lesen
// ---------------------------------------------------------------------------

/** Zerlegt CSV-Text in Zeilen aus Zellen. Beachtet Quoting, auch über Zeilenumbrüche hinweg. */
function splitCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
    } else if (ch !== '\r') {
      field += ch;
    }
  }
  // Letzte Zeile ohne abschließenden Umbruch nicht verlieren.
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** Streift die Excel-Texthülle `="…"` sowie umschließende Quotes ab und trimmt. */
function clean(value: string | undefined): string {
  let v = (value ?? '').trim();
  const formula = /^="(.*)"$/s.exec(v);
  if (formula) v = formula[1]!.replaceAll('""', '"');
  else if (v.startsWith('"') && v.endsWith('"') && v.length >= 2) v = v.slice(1, -1);
  return v.trim();
}

/** Vergleichsform eines Spaltenkopfs: klein, ohne Leerzeichen, Bindestriche und Punkte. */
function normalizeHeader(value: string): string {
  return clean(value).toLowerCase().replace(/[\s._-]/g, '');
}

// Bewusst getrennte Listen statt Teilstring-Suche: „variosku" ist die ANZEIGE der bestehenden
// Zuordnung und darf nicht als Zielspalte durchgehen, obwohl „vario" darin vorkommt.
const HEADER_ALIASES: Record<keyof Omit<ParsedAssignRow, 'index' | 'line'>, string[]> = {
  sku: ['sku', 'kauflandsku', 'artikelnummerkaufland', 'idoffer'],
  ean: ['ean', 'gtin'],
  storefront: ['storefront', 'land', 'marktplatz'],
  varioNumber: ['varioartikelnummer', 'varionummer', 'varioartikelnr', 'zielartikelnummer'],
};

/**
 * Liest eine Zuordnungsdatei.
 *
 * Toleranzen, weil die Datei durch Excel gelaufen ist: BOM am Anfang, Semikolon oder Komma als
 * Trenner, beliebige Spaltenreihenfolge, Groß-/Kleinschreibung, `="…"`-Hüllen, CRLF wie LF.
 * Unbekannte Spalten (etwa „Titel") werden ignoriert.
 *
 * Leere Zeilen fallen weg. Zeilen ohne Zielnummer bleiben erhalten — die Einstufung als
 * „nichts zu tun" macht die Vorschau, nicht der Parser.
 */
export function parseAssignCsv(text: string): ParseResult {
  const withoutBom = text.replace(/^﻿/, '');
  if (withoutBom.trim() === '') return { ok: false, error: 'Die Datei ist leer.' };

  const firstLine = withoutBom.split(/\r?\n/, 1)[0] ?? '';
  const delimiter = (firstLine.match(/,/g)?.length ?? 0) > (firstLine.match(/;/g)?.length ?? 0)
    ? ','
    : ';';

  const grid = splitCsv(withoutBom, delimiter);
  if (grid.length === 0) return { ok: false, error: 'Die Datei ist leer.' };

  const header = (grid[0] ?? []).map(normalizeHeader);
  const col = (key: keyof typeof HEADER_ALIASES): number =>
    header.findIndex((h) => HEADER_ALIASES[key].includes(h));

  const iSku = col('sku');
  const iEan = col('ean');
  const iStorefront = col('storefront');
  const iVario = col('varioNumber');

  if (iVario < 0) {
    return {
      ok: false,
      error: 'Die Spalte „Vario-Artikelnummer" fehlt. Am einfachsten die Vorlage exportieren und darin die Zielspalte ausfüllen.',
    };
  }
  if (iSku < 0 && iEan < 0) {
    return {
      ok: false,
      error: 'Es fehlt eine Spalte „SKU" oder „EAN", über die sich das Angebot zuordnen lässt.',
    };
  }

  const rows: ParsedAssignRow[] = [];
  for (let r = 1; r < grid.length; r += 1) {
    const cells = grid[r]!;
    const sku = iSku >= 0 ? clean(cells[iSku]) : '';
    const ean = iEan >= 0 ? clean(cells[iEan]) : '';
    const storefront = iStorefront >= 0 ? clean(cells[iStorefront]).toLowerCase() : '';
    const varioNumber = clean(cells[iVario]);
    if (sku === '' && ean === '' && varioNumber === '') continue; // Leerzeile
    rows.push({ index: rows.length, line: r + 1, sku, ean, storefront, varioNumber });
  }

  if (rows.length === 0) return { ok: false, error: 'Die Datei enthält keine Datenzeilen.' };
  return { ok: true, rows };
}
